/**
 * @fileoverview Shared conductor shell around the canonical inspection renderer.
 * @module inspection-renderer/shell
 *
 * Responsibilities:
 * - Present form steps, completion progress, missing items, and co-inspection
 *   notices around the package renderer.
 * - Provide responsive presentation modes without owning persistence.
 *
 * Dependencies:
 * - InspectionRenderer, FormStepTabs, and conductorProgress.
 *
 * Architecture:
 * - Hosts inject persistence, connectivity state, callbacks, and optional sync UI.
 *
 * Notes:
 * - tetherSyncSlot is opaque React content; this package does not depend on
 *   mobile TetherSync.
 *
 * @component InspectionConductorShell
 */
import { useCallback, useState, type ReactNode } from 'react';
import type { CompletionVerdict } from '@firesquirrel/core-domain';
import { InspectionRenderer } from '../InspectionRenderer';
import type { InspectionRendererProps } from '../InspectionRenderer';
import { FormStepTabs } from './FormStepTabs';
import type { InspectionVisibleFormSummary } from './FormStepTabs';
import { deriveConductorProgress } from './conductorProgress';

/**
 * @contract CoInspectionPresentation
 * Purpose: Host-provided co-inspection identity, notices, and access state.
 * Fields: isLockedByConnectivity, isReadOnly, lockedBannerMessage,
 *   primaryInspectorNotice, readOnlyNotice, coInspectorName, coInspectorStatus.
 * Rules: Connectivity lockout forces read-only presentation.
 * Sync: Values are supplied by the host's co-inspection session.
 */
export interface CoInspectionPresentation {
  isLockedByConnectivity?: boolean;
  isReadOnly?: boolean;
  lockedBannerMessage?: ReactNode;
  primaryInspectorNotice?: ReactNode;
  readOnlyNotice?: ReactNode;
  coInspectorName?: string;
  coInspectorStatus?: ReactNode;
}

/**
 * @contract InspectionConductorShellProps
 * Purpose: Renderer inputs plus conductor presentation and completion actions.
 * Fields: InspectionRenderer props, presentationMode, location, packageLabel,
 *   roleBadge, coInspection, tetherSyncSlot, completionPanelSlot,
 *   exitRecoverySlot, completeAction, onClose, saveStatus, children.
 * Rules: Completion enablement is derived from the package completion verdict.
 * Sync: Hosts own persistence and provide save state and callbacks.
 */
export interface InspectionConductorShellProps extends Omit<
  InspectionRendererProps,
  'showFormTabs'
> {
  presentationMode: 'mobile-fullscreen' | 'tenant-tablet-modal' | 'template-preview';
  /** `content-only` hides shell header, form tabs, missing items, and footer; the renderer path is unchanged. */
  chrome?: 'full' | 'content-only';
  /** Receives visible form summaries from the renderer; the shell keeps its own copy for navigation. */
  onVisibleFormsChange?: (forms: InspectionVisibleFormSummary[]) => void;
  initialFormIndex?: number;
  onFormIndexChange?: (index: number) => void;
  entityAddress?: string;
  caseNumber?: string;
  packageLabel?: string;
  roleBadge?: ReactNode;
  coInspection?: CoInspectionPresentation;
  tetherSyncSlot?: ReactNode;
  completionPanelSlot?: ReactNode;
  exitRecoverySlot?: ReactNode;
  completeAction?: {
    onComplete(): void;
    label?: string;
    isSubmitting?: boolean;
    blockedReason?: string;
  };
  onClose?: () => void;
  saveStatus?: ReactNode;
  children?: ReactNode;
}

const EMPTY_VERDICT: CompletionVerdict = { canComplete: false, blocking: [], advisory: [] };

/**
 * @component InspectionConductorShell
 * Purpose: Compose the shared renderer with conductor navigation and completion UI.
 * Props: InspectionConductorShellProps.
 * Behavior: Connectivity lockout forces the renderer into read-only mode.
 */
export function InspectionConductorShell(props: InspectionConductorShellProps): ReactNode {
  const {
    presentationMode,
    chrome = 'full',
    entityAddress,
    caseNumber,
    packageLabel,
    roleBadge,
    coInspection,
    tetherSyncSlot,
    completionPanelSlot,
    exitRecoverySlot,
    completeAction,
    onClose,
    saveStatus,
    children,
    readOnly,
    entityName,
    templateName,
    onCompletionVerdictChange,
    activeFormId: controlledActiveFormId,
    onActiveFormIdChange,
    initialFormIndex = 0,
    onFormIndexChange,
    onVisibleFormsChange,
    ...rendererProps
  } = props;
  const showChrome = chrome === 'full';
  const [visibleForms, setVisibleForms] = useState<InspectionVisibleFormSummary[]>([]);
  const handleVisibleFormsChange = useCallback((forms: InspectionVisibleFormSummary[]) => {
    setVisibleForms(forms);
    onVisibleFormsChange?.(forms);
  }, [onVisibleFormsChange]);
  const [internalActiveFormId, setInternalActiveFormId] = useState<string | undefined>();
  const [completionVerdict, setCompletionVerdict] = useState(EMPTY_VERDICT);
  const [isMissingItemsOpen, setIsMissingItemsOpen] = useState(false);
  const isConnectivityLocked = !!coInspection?.isLockedByConnectivity;
  const isReadOnly = !!readOnly || !!coInspection?.isReadOnly || isConnectivityLocked;
  const progress = deriveConductorProgress(completionVerdict, visibleForms);
  const defaultForm = visibleForms[Math.max(0, Math.min(initialFormIndex, visibleForms.length - 1))];
  const activeFormId = controlledActiveFormId ?? internalActiveFormId ?? defaultForm?.id;
  const selectForm = useCallback((id: string) => {
    if (controlledActiveFormId === undefined) setInternalActiveFormId(id);
    onActiveFormIdChange?.(id);
    const index = visibleForms.findIndex((form) => form.id === id);
    if (index >= 0) onFormIndexChange?.(index);
  }, [controlledActiveFormId, onActiveFormIdChange, onFormIndexChange, visibleForms]);
  const selectedFormIndex = Math.max(
    0,
    visibleForms.findIndex((form) => form.id === (activeFormId ?? visibleForms[0]?.id)),
  );
  const hasPreviousForm = selectedFormIndex > 0;
  const hasNextForm = selectedFormIndex < visibleForms.length - 1;

  const handleCompletionVerdictChange = useCallback((verdict: CompletionVerdict) => {
    setCompletionVerdict(verdict);
    onCompletionVerdictChange?.(verdict);
  }, [onCompletionVerdictChange]);

  const shell = (
    <div
      data-inspection-conductor-shell
      data-presentation-mode={presentationMode}
      className="flex h-full min-h-0 flex-col bg-white"
    >
      {showChrome && (
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 text-gray-900">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <div className="min-w-0 basis-full sm:basis-auto">
            <div role="heading" aria-level={1} className="line-clamp-2 text-base font-semibold leading-tight text-gray-900">
              {templateName ?? entityName ?? 'Inspection'}
            </div>
            <p className="truncate text-xs text-gray-500">
              {[entityName, entityAddress, caseNumber && `Case ${caseNumber}`].filter(Boolean).join(' · ')}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {packageLabel && (
              <span data-template-badge className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600">
                {packageLabel}
              </span>
            )}
            {roleBadge && (
              <span data-role-badge className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600">
                {roleBadge}
              </span>
            )}
            {coInspection?.coInspectorName && (
              <span data-co-inspector-pill className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-600">
                Co-inspector: {coInspection.coInspectorName}
                {coInspection.coInspectorStatus && <span> · {coInspection.coInspectorStatus}</span>}
              </span>
            )}
            <span
              role="status"
              aria-label="Inspection progress"
              data-inspection-progress-pill
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700"
            >
              <span className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-200">
                <span className="block h-full bg-green-500 transition-all duration-300" style={{ width: `${progress.percent}%` }} />
              </span>
              {progress.percent}% complete
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {tetherSyncSlot}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close inspection"
              className="shrink-0 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-200"
            >
              Close
            </button>
          )}
        </div>
      </header>
      )}

      {showChrome && visibleForms.length > 1 && (
        <div className="border-b border-gray-200">
          <FormStepTabs
            forms={visibleForms}
            activeFormId={activeFormId ?? visibleForms[0]?.id ?? ''}
            onActiveFormIdChange={selectForm}
            showIssueCounts={!isReadOnly}
          />
        </div>
      )}

      {coInspection?.primaryInspectorNotice && (
        <div role="status" data-primary-inspector-notice className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          {coInspection.primaryInspectorNotice}
        </div>
      )}
      {isConnectivityLocked && (
        <div role="alert" data-connectivity-lock className="border-b border-red-300 bg-red-50 px-4 py-2 text-sm text-red-900">
          {coInspection?.lockedBannerMessage ?? 'Editing is locked while the primary inspector is unavailable.'}
        </div>
      )}
      {isReadOnly && (
        <div role="status" data-read-only-banner className="border-b border-orange-200 bg-orange-50 px-4 py-2 text-sm text-orange-900">
          {coInspection?.readOnlyNotice ?? 'This inspection is read-only.'}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {showChrome && progress.missingItems.length > 0 && (
          <section className="border-b border-amber-200 px-4 py-2">
            <button
              type="button"
              aria-expanded={isMissingItemsOpen}
              onClick={() => setIsMissingItemsOpen((open) => !open)}
              className="text-sm font-medium text-amber-900"
            >
              Missing items ({progress.missingItems.length})
            </button>
            {isMissingItemsOpen && (
              <div role="region" aria-label="Missing items" data-missing-items className="pt-2">
                <ul className="list-disc space-y-1 pl-5 text-sm text-amber-900">
                  {progress.missingItems.map((issue, index) => {
                    const formName = visibleForms.find((form) => form.id === issue.formId)?.name;
                    const itemName = issue.label ?? issue.fieldId ?? 'Required item';
                    return (
                      <li key={`${issue.formId ?? 'form'}:${issue.fieldId ?? 'item'}:${issue.kind}:${index}`}>
                        {formName ? `${formName}: ` : ''}{itemName} — {issue.kind}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>
        )}

        <InspectionRenderer
          {...rendererProps}
          entityName={entityName}
          templateName={templateName}
          readOnly={isReadOnly}
          showFormTabs={false}
          activeFormId={activeFormId}
          onActiveFormIdChange={selectForm}
          onVisibleFormsChange={handleVisibleFormsChange}
          onCompletionVerdictChange={handleCompletionVerdictChange}
        />
        {children}
      </div>

      {completionPanelSlot && (
        <div data-completion-panel-slot className="border-t border-slate-200 bg-white px-4 py-3">
          {completionPanelSlot}
        </div>
      )}

      {exitRecoverySlot && (
        <div data-exit-recovery-slot className="border-t border-slate-200 bg-white px-4 py-3">
          {exitRecoverySlot}
        </div>
      )}

      {showChrome && (
      <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const previous = visibleForms[selectedFormIndex - 1];
              if (previous) selectForm(previous.id);
            }}
            disabled={!hasPreviousForm}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
            >
            {presentationMode === 'template-preview' ? 'Back' : 'Prev'}
          </button>
          <button
            type="button"
            onClick={() => {
              const next = visibleForms[selectedFormIndex + 1];
              if (next) selectForm(next.id);
            }}
            disabled={!hasNextForm}
            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
        <div className="flex items-center gap-3">
          {saveStatus && <div data-save-status className="text-sm text-gray-600">{saveStatus}</div>}
          {completeAction && (
            <>
              {completeAction.blockedReason && (
                <span role="status" data-completion-blocked-reason>{completeAction.blockedReason}</span>
              )}
              <button
                type="button"
                onClick={completeAction.onComplete}
                disabled={!progress.canComplete || isReadOnly || !!completeAction.isSubmitting || !!completeAction.blockedReason}
                data-can-complete={progress.canComplete}
                className="shrink-0 rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {completeAction.isSubmitting ? 'Completing…' : completeAction.label ?? 'Complete Inspection'}
              </button>
            </>
          )}
        </div>
      </footer>
      )}
    </div>
  );

  if (presentationMode === 'mobile-fullscreen') {
    return <div className="fixed inset-0 z-50 bg-white">{shell}</div>;
  }
  if (presentationMode === 'tenant-tablet-modal') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
        <section
          role="dialog"
          aria-modal="true"
          aria-label={templateName ?? entityName ?? 'Inspection'}
          className="h-full max-h-[98dvh] w-full max-w-7xl overflow-hidden rounded-xl bg-white shadow-2xl"
        >
          {shell}
        </section>
      </div>
    );
  }
  return <div className="h-full min-h-0">{shell}</div>;
}
